import { updateCategoryMenu, selectCategory } from './event-handlers/update-category-menu.js';
import { updateExistingEventSubmitButton } from './event-handlers/update-existing-event-submit-button.js';
import { updateContactListSubmitButton } from './event-handlers/update-contact-list-submit-button.js';
import { updateLastStepSubmitButton } from './event-handlers/update-last-step-submit-button.js';
import { initializeEventLocation } from './event-handlers/initialize-event-location.js';
import { initializeEventLocationAddressOrVenue } from './event-handlers/initialize-event-location-address-or-venue.js';
import { initializeEventDate } from './event-handlers/initialize-event-date.js';
import { initializeEventTime } from './event-handlers/initialize-event-time.js';
import { initializePerformanceLength } from './event-handlers/initialize-performance-length.js';
import { initializeEventDetails } from './event-handlers/initialize-event-details.js';
import { initializeBudget } from './event-handlers/initialize-budget.js';
import { initializeEventPlannerPhone } from './event-handlers/initialize-event-planner-phone.js';
import { initializeAdditionalQuestions } from './event-handlers/initialize-additional-questions.js';
import { generateRecaptchaToken } from './event-handlers/generate-recaptcha-token.js';
import { validateEventLocation } from './event-handlers/validate-event-location.js';
import { validateBudget } from './event-handlers/validate-budget.js';
import { validateEventPlannerPhone } from './event-handlers/validate-event-planner-phone.js';
import { countCharactersSelected } from './event-handlers/count-characters-selected.js';
import { revealByCheckbox } from './event-handlers/reveal-by-checkbox.js';
import { revealBySelectMenu } from './event-handlers/reveal-by-select-menu.js';
import { scrollForm } from './helpers/scroll-form.js';
import { loadFingerprint, appendFingerprint } from './event-handlers/fingerprint.js';
import { loadMaxmindMinfraudDeviceTracking, trackGtmEvent, trackMountainConversion, trackPinterestConversion, trackRedditConversion } from '../../client/components/helpers/conversions.js';

/**
 * A class which encapsulates the step-by-step Quote Request Form process.
 *
 * @depends moveTo.js (for every step transition)
 * @depends jQuery (initializeEventDate handler)
 * @depends Cookies.js (finishAndRedirect method)
 *
 * @class QuoteRequestForm
 */
class QuoteRequestForm {
	/**
	 * @param {HTMLElement} element - The HTML element for which
	 * we'd like to attach this instance of our QuoteRequestForm.
	 */
	constructor(element) {
		this.wrapperElement = element;
		this.formElement = this.wrapperElement.querySelector('form');
		this.quoteRequestStarted = false;
		this.lastErrorMessage = '';
		this.archivedSteps = {};

		this.setCurrentStep();

		// Event listeners
		this.formElement.onsubmit = null; // Remove any existing onsubmit handler
		this.formElement.addEventListener('submit', this.handleSubmit.bind(this));
		this.formElement.addEventListener('change', this.checkInput.bind(this));
		this.formElement.addEventListener('focusout', this.checkInput.bind(this));
		this.formElement.addEventListener('click', this.handleClick.bind(this));

		// Has the form already been submitted? An inline onsubmit
		// handler can set a data attribute indicating such on the form
		// element. This lets us trigger submission of the first step
		// as soon as this JS loads and executes.
		if (this.formElement.dataset.hasSubmitted === 'true') {
			this.handleSubmit();
		}

		this.trackEvent('initialized');
	}

	handlers = {
		updateCategoryMenu,
		selectCategory,
		updateExistingEventSubmitButton,
		updateContactListSubmitButton,
		updateLastStepSubmitButton,
		initializeEventLocation,
		initializeEventLocationAddressOrVenue,
		initializeEventDate,
		initializeEventTime,
		initializePerformanceLength,
		initializeEventDetails,
		initializeBudget,
		initializeEventPlannerPhone,
		initializeAdditionalQuestions,
		generateRecaptchaToken,
		validateEventLocation,
		validateBudget,
		validateEventPlannerPhone,
		countCharactersSelected,
		revealByCheckbox,
		revealBySelectMenu,
		appendFingerprint
	}

	/**
	 * Call given handlers if they exist within our `this.handlers` object.
	 *
	 * @name callHandler
	 * @param {String} handler
	 * @param {*} event or callback arguments
	 */
	callHandler(handler, ...args) {
		if (!handler) {
			return;
		}

		// We support one or more handlers, separated by commas.
		const handlerNames = handler.split(',');

		for (const handlerName of handlerNames) {
			if (handlerName && typeof this.handlers[handlerName] === 'function') {
				console.log('Calling event handler:', handlerName, 'Args:', args);
				this.handlers[handlerName].call(this, ...args);
			}
		}
	}

	/**
	 * Ensure the `this.currentStepElement` property up to date plus
	 * other housekeeping. It's intended to call this whenever we've
	 * switched to a new step.
	 *
	 * @name setCurrentStep
	 * @method
	 */
	setCurrentStep() {
		this.currentStepElement = this.wrapperElement.querySelector('.quote-request-form__step[data-current-step]');

		// We'll disable all inputs on any existing future steps
		// and enable all inputs on this current step. This ensures
		// we don't get stopped by any HTML5 validation rules on
		// future steps that are not currently visible.
		let disableStepInputs = false;
		for (const stepElement of this.getSteps()) {
			if (stepElement.dataset.currentStep && !disableStepInputs) {
				for (const inputElement of stepElement.querySelectorAll('input, select, textarea')) {
					inputElement.disabled = false;
				}

				disableStepInputs = true;
				continue;
			}

			if (disableStepInputs) {
				for (const inputElement of stepElement.querySelectorAll('input, select, textarea')) {
					inputElement.disabled = true;
				}
			}
		}

		// Restore this step. This will return any input element
		// selections or values back to their previous values/choices
		// in cases where we've archived a step and just re-fetched it.
		this.restoreStep(this.currentStepElement.dataset.stepName);

		// Remove any existing Ladda instance.
		if (this.ladda) {
			this.ladda.remove();
		}

		// Set up Ladda with the current step's next button.
		const nextStepButton = this.currentStepElement.querySelector('.js-quote-step-next');
		this.ladda = Ladda.create(nextStepButton);

		// Call possible onload event handler for this step.
		this.callHandler(this.currentStepElement.dataset.onload);

		console.log('Current step is now:', this.currentStepElement);

		// Track having moved to a new step.
		const firstStepElement = this.getSteps()[0];
		if (firstStepElement.dataset.stepName !== this.currentStepElement.dataset.stepName) {
			this.trackEvent('next_step');
		}
	}

	/**
	 * Given a click event, what would we like to do with it?
	 *
	 * @name handleClicks
	 * @method
	 * @param {Event} event
	 */
	handleClick(event) {
		// At this time all we care about are back button clicks.
		if (event.target.matches('.js-quote-step-back')) {
			this.goToPreviousStep();
		}
	}

	/**
	 * "Submit" the step we're currently on. If there is a problem
	 * then we'll halt and show errors. Otherwise, figure out how to
	 * proceed.
	 *
	 * @name handleSubmit
	 * @method
	 * @param {Event} event - Optional
	 */
	handleSubmit(event) {
		if (event) {
			event.preventDefault();
		}

		// Mark form as having been submitted regardless of whether
		// there are any validation errors or not.
		this.formElement.dataset.hasSubmitted = 'true';

		console.log('Submit step event:', event);

		this.ladda.start();

		this.validateCurrentStep(errors => {
			console.log('Done validating current step.');
			const nextStepButton = this.currentStepElement.querySelector('.js-quote-step-next');

			if (errors) {
				this.showStepErrors(errors);
			} else if (nextStepButton.dataset.submit === 'true') {
				const quoteRequest = this.collect();

				console.log('Submitting the current quote request!');

				let endpoint = '/submit_search_inquiry';

				if (quoteRequest.get('quote_request_submitted')) {
					// We're working with additional questions, now...
					endpoint = '/submit_additional_request_info';
				}

				fetch(endpoint, {
					method: 'POST',
					headers: {
						'Content-Type': 'application/x-www-form-urlencoded',
					},
					body: quoteRequest
				})
				.then(response => {
					if (!response.ok) {
						throw new Error(`Quote request network response was not ok, status code ${response.status}.`);
					}
					return response.json();
				})
				.then(this.handleSubmitResponse.bind(this))
				.catch(error => {
					const publicError = new Error('Hm, we’re having trouble submitting your quote request... try again?');
					this.showStepErrors([publicError]);

					console.error('Submission error:', error);
				});

			} else {
				this.goToNextStep();
			}
		});
	}

	/**
	 * After we've submitted the quote request we'll handle the response:
	 *
	 * - Check for errors in the response and show them if we have them.
	 * - Run 3rd part conversions (Google Tag Manager, MNTN, Pinterest, etc?)
	 * - Redirect if no additional questions or load additional questions.
	 *    a. If we load additional questions, remove prior steps.
	 * - Stop Ladda.
	 *
	 * @name handleSubmitSuccess
	 * @method
	 *
	 * @param {Object} response - Data from the back end describing the
	 * result of submission of the quote request.
	 */
	handleSubmitResponse(response) {
		console.log('Quote request submission response:', response);

		// Add a breadcrumb for Bugsnag to help debug the response
		// in cases when there are errors.
		bugsnagClient.leaveBreadcrumb('Submission response', {
			response: JSON.stringify(response)
		});

		// Check status for errors or success...
		if (response.status.includes('error')) {
			let errorMessage = 'Your quote request has some errors.';

			// Unique errors...
			if (typeof response.errors.excluded_foreign_promokits === 'string') {
				errorMessage = response.errors.excluded_foreign_promokits;
			} else if (typeof response.errors.check_login_attempt === 'string') {
				errorMessage = response.errors.check_login_attempt;
			}

			// If the response includes `error_to_step_names_map`
			// we'll use it to determine which step to go to.
			if (typeof response.error_to_step_names_map !== 'undefined') {
				const errorStepNames = response.error_to_step_names_map;

				// Since our process is step-by-step we only handle
				// one error at a time: the first. It's likely that
				// the first error is also the only error.
				const firstError = Object.keys(response.errors)[0];

				// If we have a map between the first error and a
				// step then let's go to that step.
				if (typeof errorStepNames[firstError] !== 'undefined') {
					this.goToStep(errorStepNames[firstError]);
					errorMessage = response.errors[firstError];
				}
			} else if (response.errors.length) {
				// Fall back to concatenating the errors into a string.
				errorMessage = response.errors.join(', ');
			}

			const error = new Error(errorMessage);
			this.showStepErrors([error]);
		} else if (response.status === 'banned') {
			const error = new Error(response.ban_message);
			this.showStepErrors([error]);
		} else if (response.status === 'duplicate') {
			const error = new Error(response.errors.duplicate_message);
			this.showStepErrors([error]);
		} else if (response.status === 'recaptcha') {
			const error = new Error(response.errors.recaptcha_message);
			this.showStepErrors([error]);
		} else if (response.status === 'invalid_event') {
			// One day we'll be able to use optional chaining...
			// https://developer.mozilla.org/en-US/docs/Web/JavaScript/Reference/Operators/Optional_chaining
			if (response.error_to_step_names_map && typeof response.error_to_step_names_map[response.status] !== 'undefined') {
				this.goToStep(response.error_to_step_names_map[response.status]);
			}
			const error = new Error('The previous request you have chosen is invalid.');
			this.showStepErrors([error]);
		} else if (response.status === 'success') {
			console.log('Quote request submitted successfully!');

			// Did we submit a quote request or additional questions?
			// The core submission returns an `event_ID` property,
			// among others, which are unique.
			if (response.event_ID) {
				// Successfully submitted quote request.
				// Next up...

				// We want some hidden input elements to indicate
				// successful submission and store relevant IDs.
				// `<input type="hidden" name="quote_request_submitted" value="true">`;
				let quoteRequestSubmittedElement = document.createElement('input');
				quoteRequestSubmittedElement.type = 'hidden';
				quoteRequestSubmittedElement.name = 'quote_request_submitted';
				quoteRequestSubmittedElement.value = 'true';
				this.formElement.insertAdjacentElement('afterbegin', quoteRequestSubmittedElement);

				// `<input type="hidden" name="events_ID" value="${response.event_ID}">`;
				let eventsIDElement = document.createElement('input');
				eventsIDElement.type = 'hidden';
				eventsIDElement.name = 'events_ID';
				eventsIDElement.value = response.event_ID;
				this.formElement.insertAdjacentElement('afterbegin', eventsIDElement);

				// `<input type="hidden" name="event_roles_ID" value="${response.role_ID}">`;
				let eventRolesIDInput = document.createElement('input');
				eventRolesIDInput.type = 'hidden';
				eventRolesIDInput.name = 'event_roles_ID';
				eventRolesIDInput.value = response.role_ID;
				this.formElement.insertAdjacentElement('afterbegin', eventRolesIDInput);

				// No longer submit the form since it was successful.
				const nextStepButton = this.currentStepElement.querySelector('.js-quote-step-next');
				delete nextStepButton.dataset.submit;

				// Notate that the core submission has been made.
				nextStepButton.dataset.quoteRequestSubmitted = 'true';

				// Track submission of the core quote request.
				this.trackEvent('submission', { response });
				this.goToNextStep();
			} else {
				// Successfully submitted additional questions.

				// Track additional questions completed...
				this.trackEvent('submission_additional_questions');

				// All done!
				this.finishAndRedirect();
			}
		}
	}

	/**
	 * Initiate the sequence of events that will attempt taking the
	 * user from the step they are currently on to the next logical
	 * step in the quote request process.
	 *
	 * We'll make ajax requests as necessary to load (or reload?)
	 * subsequent steps based upon what the current step is and/or
	 * what data our current quote request form contains.
	 *
	 * @name goToNextStep
	 * @method
	 */
	goToNextStep() {
		// Is the next step already here in the DOM?
		let nextStepElement = this.getAdjacentStepElement(this.currentStepElement, 'next');

		if (nextStepElement && this.currentStepElement.dataset.loadNextSteps !== 'always') {
			console.log('Going to existing next step:', nextStepElement);
			this.transitionToStep(nextStepElement);
		} else {
			this.loadNextSteps((error) => {
				if (error) {
					this.showStepErrors([error]);
					return;
				}

				this.clearErrors();
				nextStepElement = this.getAdjacentStepElement(this.currentStepElement, 'next');

				if (nextStepElement) {
					console.log('Going to newly added step:', nextStepElement);
					this.transitionToStep(nextStepElement);
				} else {
					const nextStepButton = this.currentStepElement.querySelector('.js-quote-step-next');
					if (nextStepButton.dataset.submit !== 'true' && nextStepButton.dataset.quoteRequestSubmitted !== 'true') {
						// Allow one more check for additional questions to catch the edge case where
						// we did not know we were on the last step until the current step was sent.
						// This can happen if answers on the current step result in no further steps.
						nextStepButton.dataset.submit = 'true';
						this.handleSubmit();
					} else {
						// There's no next step which means there's nothing
						// else to do except finish up!
						this.finishAndRedirect();
					}
				}
			});
		}
	}

	/**
	 * Initiate the sequence of events that will attempt taking the
	 * user from the step they are currently on to the previous logical
	 * step in the quote request process.
	 *
	 * Unlike `goToNextStep` this function is relatively simple: there
	 * is no logic when going backwards as our quote request form
	 * currently exists.
	 *
	 * @name goToPreviousStep
	 * @method
	 */
	goToPreviousStep() {
		const previousStepElement = this.getAdjacentStepElement(this.currentStepElement, 'previous');
		if (previousStepElement) {
			console.log('Going back to previous step...');
			this.transitionToStep(previousStepElement);
		}
	}

	/**
	 * Get the adjacent step element in the DOM that is a step in the quote
	 * request process or null if no adjacent element exists. This skips any elements that are not
	 * a step in the process (e.g. stuff the LastPass browser extension inserts).
	 *
	 * @name getAdjacentStepElement
	 * @method
	 *
	 * @param {HTMLElement} currentStepElement The current step element
 	 * @param {string} direction The direction of traversal: 'previous' or 'next'
	 */
	getAdjacentStepElement(currentStepElement, direction) {
		const property = direction === 'previous' ? 'previousElementSibling' : 'nextElementSibling';
		let adjacentStepElement = currentStepElement[property];

		while (adjacentStepElement && !adjacentStepElement.matches('.quote-request-form__step')) {
			console.log('Disregard adjacent step:', adjacentStepElement);
			adjacentStepElement = adjacentStepElement[property];
		}

		return adjacentStepElement;
	}

	/**
	 * Take the user to a specific step given the step name or to the
	 * first matching step given an array of step names. Nothing will
	 * happen if this is called with a step name that doesn't exist or
	 * a series of step names where none exist.
	 *
	 * @name goToStep
	 * @method
	 *
	 * @param {String|Array[String]} stepName
	 */
	goToStep(stepName) {
		let targetStepElement = null;

		if (Array.isArray(stepName)) {
			for (const name of stepName) {
				targetStepElement = this.getStep(name);
				if (targetStepElement) {
					break;
				}
			}
		} else {
			targetStepElement = this.getStep(stepName);
		}

		if (targetStepElement) {
			console.log(`Going to specific step: ${stepName}...`);
			this.transitionToStep(targetStepElement);
		}
	}

	/**
	 * Make an ajax request to fetch the next quote request steps
	 * given the current quote request.
	 *
	 * @name loadNextSteps
	 * @method
	 *
	 * @param {Function} callback - A callback function invoked with (err).
	 */
	loadNextSteps(callback) {
		const quoteRequest = this.collect();

		fetch('/ajax/get-next-quote-request-form-steps', {
			method: 'POST',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			},
			body: quoteRequest
		})
			.then(response => {
				if (!response.ok) {
					throw new Error('Hm, we couldn’t load the next step... try again?');
				}
				return response.text();
			})
			.then(stepContent => {
				// The back end will intentionally return nothing when
				// there are no further steps. This signals that there
				// is nothing left to do, which we'll check for within
				// the `goToNextStep()` method.
				if (!stepContent.length) {
					console.log('No step(s) returned.');
				}

				// Archive any existing steps that are following the
				// current step because we received new step content
				// to replace them with (or there are now none).
				let archiveStep = false;
				for (const step of this.getSteps()) {
					if (step.matches('[data-current-step]')) {
						archiveStep = true;
					} else if (archiveStep) {
						console.log('Archiving existing future step:', step);
						this.archiveStep(step);
					}
				}

				// Append the new stepContent, if any, after the current step.
				if (stepContent.length) {
					this.currentStepElement.insertAdjacentHTML('afterend', stepContent);
					console.log('New step(s) inserted after current step!');
				}

				callback();
			})
			.catch(callback);
	}

	/**
	 * Given a step element, transition to that step.
	 *
	 * Transitioning to a step consists of manipulating some classes,
	 * various tasking, and upkeep of the current class instance.
	 *
	 * @name transitionToStep
	 * @method
	 *
	 * @param {HTMLElement} stepElement - The step to transition to.
	 */
	transitionToStep(stepElement) {
		// The `js-hide` class applies all applicable styles and the
		// `invisible` class ensures no tabbing to hidden step inputs.
		this.currentStepElement.classList.add('js-hide', 'invisible');

		// Explicitly remove fade-in class instead of using `.replace()`
		// since it may or may not be set on the current step. We want
		// to remove it because when or if we transition to this step
		// again later it will be re-applied so that the fade-in
		// animation happens each time.
		this.currentStepElement.classList.remove('quote-request-form__step--fade-in');

		// Remove data-current-step attribute.
		delete this.currentStepElement.dataset.currentStep;

		// All steps that are not the current step will have the
		// `js-hide` class so we can use `.replace()` here.
		stepElement.classList.replace('js-hide', 'quote-request-form__step--fade-in');
		stepElement.classList.remove('invisible');

		stepElement.dataset.currentStep = true;
		this.setCurrentStep();

		this.updateProgressIndicator();

		// Once we've transitioned to a new step we want to signal that
		// the process has started and do a couple things.
		if (!this.quoteRequestStarted) {
			this.quoteRequestStarted = true;

			// Alert users about information loss for the remainder of
			// the page lifetime.
			// See: https://developer.mozilla.org/en-US/docs/Web/API/WindowEventHandlers/onbeforeunload
			window.onbeforeunload = () => 'Your information will be lost if you leave this page.';

			// Adjust the `viewport` meta tag for iOS Safari, which
			// otherwise will auto-zoom the page whenever the user
			// focuses one of our input fields.
			// See: https://gigsalad.slack.com/archives/C08UD8Q56/p1689947389576729
			// See: https://stackoverflow.com/a/57527009
			const addMaximumScaleToMetaViewport = () => {
				const viewportTag = document.querySelector('meta[name=viewport]');
				if (viewportTag) {
					let viewportContent = viewportTag.getAttribute('content');
					let maxScaleRegex = /maximum\-scale=[0-9\.]+/g;

					if (maxScaleRegex.test(viewportContent)) {
						viewportContent = viewportContent.replace(maxScaleRegex, 'maximum-scale=1.0');
					} else {
						viewportContent = `${viewportContent}, maximum-scale=1.0`;
					}

					viewportTag.setAttribute('content', viewportContent);
				}
			};

			// iOS detection from https://stackoverflow.com/a/9039885
			const isIOS = () => {
				const agents = [
					'iPad Simulator',
					'iPhone Simulator',
					'iPod Simulator',
					'iPad',
					'iPhone',
					'iPod'
				];
				return agents.includes(navigator.platform) || (navigator.userAgent.includes('Mac') && 'ontouchend' in document);
			};

			if (isIOS()) {
				console.log('This appears to be an iOS device. Adjusting viewport to have `maximum-scale=1.0`.');
				addMaximumScaleToMetaViewport();
			}

			// Adjust DOM nodes to match things done in the existing
			// quote request process so that CSS selectors continue to
			// match/work as desired.

			// Some pages have this "results header alt styling" wrapper...
			const resultsHeader = document.querySelector('.results-header__alt-styling');
			if (resultsHeader) {
				// Setting a `data-step-number` attribute on the results
				// header causes all manner of selectors to change with
				// various new styles. It's strange and would be nice to
				// rework one day.
				resultsHeader.dataset.stepNumber = '';

				// Ensure we have padding at the top so that tall steps
				// will not touch the top of the container on relevant
				// pages.
				this.wrapperElement.classList.add('soft--top');
			}

			// This element is seemingly expected to be manually hidden.
			const quoteFormHeader = document.getElementById('js-quote-form-header');
			if (quoteFormHeader) {
				quoteFormHeader.classList.add('js-hide', 'invisible');
			}
		}

		// Scroll the form into place.
		scrollForm(this.formElement);
	}

	/**
	 * Once we're done we want to wrap things up and redirect the user
	 * to their control panel.
	 *
	 * - Remove unload callback
	 * - Set relevant cookie
	 * - Tracking
	 * - Redirect
	 *
	 * @name finishAndRedirect
	 * @method
	 */
	finishAndRedirect() {
		console.log('All done! Redirecting to control panel...');

		window.onbeforeunload = false;

		// Set a cookie for displaying the successful quote request
		// modal within the event planner control panel.
		Cookies.set('show_quote_requested_modal', true, { expires: 600 }); // Expires in 10 minutes

		// Track final completion, then redirect after waiting
		// for 2 seconds to give tracking time to happen.
		this.trackEvent('finished', { delay: 2000 }, () => {
			window.location.href = '/event-planner';
		});
	}

	/**
	 * Archive any existing steps that come after the step with the
	 * given step name.
	 *
	 * @name archiveStepsAfter
	 * @method
	 *
	 * @param {String} stepName
	 */
	 archiveStepsAfter(stepName) {
		let archiveSteps = false;
		for (const stepElement of this.getSteps()) {
			if (stepElement.matches(`[data-step-name="${stepName}"]`)) {
				archiveSteps = true;
				continue;
			}

			if (archiveSteps) {
				this.archiveStep(stepElement);
			}
		}
	}

	/**
	 * Archive the given step. Archiving a step involves two things:
	 *
	 * 1. Storing the step's input values within `this.archivedSteps`
	 * 2. Removing the step element from the DOM
	 *
	 * @param {HTMLElement} stepElement
	 */
	archiveStep(stepElement) {
		const stepName = stepElement.dataset.stepName;
		const inputElements = stepElement.querySelectorAll('input, select, textarea');
		let inputValues = {};

		for (const inputElement of inputElements) {
			if (inputElement.matches('[type="checkbox"], [type="radio"]')) {
				if (!inputValues[inputElement.name]) {
					inputValues[inputElement.name] = {};
				}

				inputValues[inputElement.name][inputElement.value] = inputElement.checked;
			} else {
				inputValues[inputElement.name] = inputElement.value;
			}
		}

		this.archivedSteps[stepName] = inputValues;
		stepElement.remove();

		console.log(`Archived ${stepName} step, prior input values were:`, inputValues);
	}

	/**
	 * Given a step name, check to see if it has been archived. If it
	 * has then restore its input values.
	 *
	 * @param {String} stepName
	 */
	restoreStep(stepName) {
		const archivedValues = this.archivedSteps[stepName];
		if (!archivedValues) {
			return;
		}

		for (const inputElement of this.currentStepElement.querySelectorAll('input, select, textarea')) {
			if (archivedValues[inputElement.name]) {
				if (inputElement.matches('[type="checkbox"], [type="radio"]')) {
					if (archivedValues[inputElement.name][inputElement.value]) {
						inputElement.checked = archivedValues[inputElement.name][inputElement.value];
					}
				} else {
					inputElement.value = archivedValues[inputElement.name];
				}
			}
		}

		console.log(`Restored input values for ${stepName} step:`, archivedValues);
	}

	/**
	 * Update the progress indicator element on top of the form.
	 *
	 * @name updateProgressIndicator
	 * @method
	 */
	updateProgressIndicator() {
		// If we're transitioning to the first step then we want to hide
		// the progress indicator. Otherwise, we want to show it.
		const progressElement = this.wrapperElement.querySelector('.step-progress');
		const firstStepElement = this.getSteps()[0];
		if (firstStepElement.dataset.stepName === this.currentStepElement.dataset.stepName) {
			progressElement.classList.add('js-hide');
			progressElement.classList.remove('quote-request-form__step--fade-in');
		} else if (progressElement.classList.contains('js-hide'))  {
			progressElement.classList.replace('js-hide', 'quote-request-form__step--fade-in');
		}

		const percentComplete = this.currentStepElement.dataset.percentageComplete;
		if (!percentComplete || percentComplete < 0 || percentComplete > 100) {
			// Skip doing anything if the percentage doesn't make sense.
			return;
		}

		const progressText = progressElement.querySelector('.progress-indicator-text');
		const progressCirclePath = progressElement.querySelector('.progress-indicator-circle-path');

		// Note: The magic number `295` here must match that in the
		// progress indicator SVG element.
		const dasharrayTotal = 295;
		const percentage = percentComplete / 100;
		const dasharrayLength = dasharrayTotal * percentage;

		console.log(`Updating progress indicator to ${percentComplete}%, new dasharray length: ${dasharrayLength}`)

		progressCirclePath.style.strokeDasharray = `${dasharrayLength}px, 295px`;
		progressText.textContent = `${percentComplete}%`;
	}

	/**
	 * Run front end validation rules for all relevant elements within
	 * the current step.
	 *
	 * @name validateCurrentStep
	 * @method
	 *
	 * @param {Function} callback - A callback function invoked with (err).
	 */
	validateCurrentStep(callback) {
		// Validate any elements with a `data-validate` attribute
		const inputs = this.currentStepElement.querySelectorAll('[data-validate]');
		console.log('Inputs in current step to validate:', inputs);

		// A few things happening...
		// 1. Convert the `inputs` NodeList into an array.
		// 2. Call `this.validateInput()` on each input.
		// 3. Filter out inputs which do not have an error.
		let inputErrors = Array.from(inputs, this.validateInput.bind(this)).filter(error => error);

		if (inputErrors.length) {
			callback(inputErrors);
		} else {
			// Call potential onsubmit handler for this step.
			if (this.currentStepElement.dataset.onsubmit) {
				this.callHandler(this.currentStepElement.dataset.onsubmit, callback);
			} else {
				callback();
			}
		}
	}

	/**
	 * Given an event, check the relevant form input element, to decide
	 * if we want to run validation rules on it. We'll ignore certain
	 * input/event combinations and run validation on the input element
	 * otherwise.
	 *
	 * @name checkInput
	 * @method
	 *
	 * @param {Event} event - A DOM event triggered on an input element.
	 */
	checkInput(event) {
		const element = event.target;

		// Don't check this input if it's one of our buttons
		if (element.matches('.js-quote-step-back, .js-quote-step-next')) {
			return;
		}

		// Don't check inputs if they're ignoring this event.
		if (element.dataset.ignoreEvent && element.dataset.ignoreEvent.includes(event.type)) {
			return;
		}

		console.log('Check input from event:', event);

		const error = this.validateInput(element);

		if (error) {
			this.showStepErrors([error]);
		} else {
			this.clearErrors(element);

			// If there are no errors then let's run any handlers
			// that may be assigned on this element.
			if (event.type === 'change') {
				this.callHandler(element.dataset.onchange, event);
			}
		}
	}

	/**
	 * @name validateInput
	 * @method
	 *
	 * @param {HTMLElement} element - The input element to validate.
	 */
	validateInput(element) {
		// If the element doesn't have a `data-validate` attribute or
		// it's disabled then we don't need to validate it.
		if (!element.matches('[data-validate]') || element.disabled) {
			return;
		}

		let name = 'Field';
		if (element.matches('[data-input-name]')) {
			name = element.dataset.inputName;
		}

		console.log('Validating input for field:', name);
		const value = element.value;

		// HTML5 validation
		if (element.hasAttribute('required') && (!element.checkValidity() || !value)) {
			let message = `${name} is required`;
			if (element.matches('select')) {
				message = 'Please choose an answer.';
			}

			let error = new Error(message);

			if (element.type === 'radio' || element.type === 'checkbox') {
				error.relatedElement = element;
			} else {
				error.highlightElement = element;
			}

			return error;
		}

		// Extra validation rules.
		const validationRules = element.dataset.validate.split(',').map(rule => rule.trim());
		console.log('Validation rules:', validationRules);

		const validationFns = validationRules.map(this.getRuleValidationFn.bind(this));

		// Return the first validation error we get.
		for (const validationFn of validationFns) {
			const errorMessage = validationFn(name, value, element);
			if (errorMessage) {
				let error = new Error(errorMessage);

				if (element.type === 'hidden') {
					// If the validated element is hidden then we can't
					// highlight it. So, look for a related autosuggest
					// element that we can highlight instead.
					error.highlightElement = this.currentStepElement.querySelector(`[data-autosuggest-result-target-id="${element.id}"]`);
					// Are there any other possible related elements
					// we want to look for here?
				} else {
					error.highlightElement = element;
				}

				return error;
			}
		}
	}

	/**
	 * @name getRuleValidationFn
	 * @method
	 *
	 * @param {String} rule - The validation rule name.
	 * @return {Function} - To be invoked with (name, value, element).
	 */
	getRuleValidationFn(rule) {
		if (rule === 'numeric') {
			const isNumeric = (name, value) => {
				if (isNaN(value)) {
					return `${name} must be a number.`;
				}
				return '';
			};
			return isNumeric;
		} else if (rule === 'required') {
			const isRequired = (name, value, element) => {
				if (!value.trim()) {
					if (element.matches('select')) {
						return 'Please choose an answer.';
					} else {
						return `${name} is required.`;
					}
				}
				return '';
			};
			return isRequired;
		}

		// Default rule always returns as valid (no message)
		const defaultValidationFn = () => ('');
		return defaultValidationFn;
	}

	/**
	 * Collect all of the quote request form input keys and values
	 * plus additional aspects of our quote request "state" into an
	 * instance of URLSearchParams for easy form submission.
	 *
	 * @name collect
	 * @method
	 * @return {URLSearchParams}
	 */
	collect() {
		let formData = new FormData(this.formElement);

		// Including the current step helps the back end
		// determine what the next step should be.
		formData.set('current_step', this.currentStepElement.dataset.stepName);

		// Our back end expects contact lists to be submitted as a
		// string that is the JSON encoded array, called `also_contact`.
		// We don't want to overwrite this if we already have a value
		// set for it, which will be the case in at least one situation:
		// when there's no contact list and we are on a profile.
		const contactListVendors = formData.getAll('contact_list_vendors[]');
		formData.set('also_contact', JSON.stringify(contactListVendors));

		// Similarly, our back end expects the event category to be
		// submitted as a string that is a JSON encoded array called
		// `event_category`.
		let eventCategory = [];
		const categoryId = formData.get('category_id');
		if (categoryId) {
			eventCategory = [categoryId];
		}
		formData.set('event_category', JSON.stringify(eventCategory));

		// The back end currently relies upon us telling it whether we
		// want to "contact other acts" (aka syndicate this request) in
		// all cases.
		// TODO: Determining the value for `contact_other_acts`
		// could/should be done on the back end because it's redundant
		// specifying this here in combination with `syndicate_up_to`
		// and the rest of the data being submitted.
		const syndicateUpTo = formData.get('syndicate_up_to');
		const context = formData.get('context');

		let contactOthers = 0;
		if ((syndicateUpTo && syndicateUpTo > 0) || (context !== 'profile' && !contactListVendors.length)) {
			contactOthers = 1;
		}

		// If we do want to contact others yet `syndicate_up_to` isn't
		// set (e.g. the step wasn't included in this context) then we
		// want to default it to `10`.
		if (syndicateUpTo === null && contactOthers) {
			formData.set('syndicate_up_to', 10);
		}

		formData.set('contact_other_acts', contactOthers);

		// The "additional questions" back end expects the event role ID
		// to be submitted simply as `role`.
		formData.set('role', formData.get('event_roles_ID') || '');

		return new URLSearchParams(formData);
	}

	/**
	 * Get an array of all existing step elements.
	 *
	 * @name getSteps
	 * @method
	 *
	 * @return {Array[Element]}
	 */
	getSteps() {
		return this.wrapperElement.querySelectorAll('.quote-request-form__step');
	}

	/**
	 * Given a name, get a step element if one matching the name exists.
	 *
	 * @name getStep
	 * @method
	 *
	 * @param {String} stepName
	 */
	getStep(stepName) {
		return this.wrapperElement.querySelector(`.quote-request-form__step[data-step-name="${stepName}"]`);
	}

	/**
	 * Gather the values used for conversion tracking.
	 *
	 * This is awkward, but attempts to abstract the conversion and
	 * tracking strings from the old quote request process.
	 * See: client/gs-apps/quote-request/main.jsx
	 *
	 * @name getConversionTrackingValues
	 * @method
	 *
	 * @param {Object} response HTTP response, if applicable.
	 */
	getConversionTrackingValues(response = {}) {
		const quoteRequest = this.collect();

		let stepNamePrevious = '';
		const previousStepElement = this.currentStepElement.previousElementSibling;
		if (previousStepElement && previousStepElement.matches('.quote-request-form__step')) {
			stepNamePrevious = previousStepElement.dataset.stepName;
		}

		const context = quoteRequest.get('context');
		const contextCapitalized = context[0].toUpperCase() + context.slice(1);
		const contactSourceSplit = quoteRequest.get('contact_source').split(':');

		// Google analytics values
		const googleAnalytics = {
			label: quoteRequest.get('current_step')
		};

		// Facebook values
		const facebook = {
			category: `EventPlanner${contextCapitalized}QuoteRequestStep`,
			action: quoteRequest.get('category_id') || quoteRequest.get('contact_list_vendors[]') || '',
			label: contactSourceSplit[contactSourceSplit.length - 1]
		}

		const conversionId = `event-role-${quoteRequest.get('event_roles_ID')}`;
		const accountId = response?.event_planner?.accounts_ID ?? '';
		const groupId = accountId ? `ac-${accountId}` : '';
		const emailAddressSha256 = response?.event_planner?.email_sha256 ?? '';
		const urlParams = new URLSearchParams(window?.location?.search);
		const pinterestClickId = urlParams.get('epik') ?? '';

		const pinterest = {
			leadType: 'Quote Request Conversion',
			clickId: pinterestClickId,
			eventId: conversionId,
			groupId,
			email: emailAddressSha256
		};

		const reddit = {
			eventType: 'Lead',
			matchKeys: {
				email: emailAddressSha256,
				conversionId,
			}
		};

		return {
			googleAnalytics,
			facebook,
			pinterest,
			reddit,
			emailAddressSha256,
			stepNamePrevious,
			context,
		}
	}

	/**
	 * Given a specific event that happens during the quote request
	 * process we want to track it in various ways.
	 *
	 * This should mirror the tracking as implemented in the old quote
	 * request process.
	 * See: client/gs-apps/quote-request/main.jsx
	 *
	 * @name trackEvent
	 * @method
	 *
	 * @param {String} event - The name of the quote request "event"
	 * that is to be "tracked" in whatever ways desired.
	 * @param {Object} options - An options object that might have
	 * various properties:
	 * - delay - A number of milliseconds to wait before calling the
	 *   callback function, which is otherwise called immediately. We use
	 *   this to allow third party tracking requests time to complete
	 *   before proceeding.
	 * - response - An HTTP request response, when applicable.
	 * @param {Function} callback - Optional. A callback function
	 * invoked with ().
	 */
	trackEvent(event, options, callback) {
		const delay = (options && options.delay) || 0;
		const trackingValues = this.getConversionTrackingValues(options?.response ?? {});

		console.log('Tracking event:', event);

		// Catch any possible tracking errors, which we don't want
		// to expose in any way.
		try {
			if (event === 'initialized') {
				trackGtmEvent({
					event: 'ga_event',
					category: 'Quote Request',
					action: 'initialized',
					label: trackingValues.context
				});

				trackGtmEvent({
					event: 'ga_event',
					category: 'Quote Request',
					action: 'step_viewed',
					label: trackingValues.googleAnalytics.label
				});
			} else if (event === 'next_step') {
				// Trigger loading MaxMind minFraud device tracking.
				// Safe to call here because it only triggers loading the
				// first time.
				loadMaxmindMinfraudDeviceTracking();

				// Trigger loading of the Fingerprint JavaScript agent library.
				// Similarly safe as minFraud above.
				loadFingerprint();

				trackGtmEvent({
					event: 'ga_event',
					category: 'Quote Request',
					action: 'step_completed',
					label: trackingValues.stepNamePrevious
				});

				trackGtmEvent({
					event: 'ga_event',
					category: 'Quote Request',
					action: 'step_viewed',
					label: trackingValues.googleAnalytics.label
				});

				trackGtmEvent({
					event: 'facebook_event',
					category: trackingValues.facebook.category,
					action: trackingValues.facebook.action,
					label: trackingValues.facebook.label
				});
			} else if (event === 'submission') {
				// See similar assignment in `html.php`
				dataLayer.push({user_data: {sha256_email_address: trackingValues.emailAddressSha256}});

				trackGtmEvent({
					event: 'facebook_event',
					category: 'Lead'
				});

				trackGtmEvent({
					event: 'facebook_event',
					category: trackingValues.facebook.category,
					action: 'Quote Request Completed',
					value: '0.00'
				});

				trackGtmEvent({
					event: 'ga_event',
					category: 'Quote Request Conversion',
					action: '',
					label: '',
					value: '0.00'
				});

				trackGtmEvent({
					event: 'Quote Request Conversion',
					category: 'Event planner',
					action: 'Requested quotes',
					label: '',
					value: '0.00'
				});

				// We'll continue to consider MNTN conversions as having 0 value.
				trackMountainConversion({ value: 0 });
				trackPinterestConversion(trackingValues.pinterest);
				trackRedditConversion(trackingValues.reddit);
			} else if (event === 'submission_additional_questions') {
				trackGtmEvent({
					event: 'ga_event',
					category: 'Quote Request',
					action: 'additional_questions_completed',
					label: 'Quote Form Additional Questions Completed'
				});
			} else if (event == 'finished') {
				trackGtmEvent({
					event: 'ga_event',
					category: 'Quote Request',
					action: 'step',
					label: 'Quote Form Completed'
				});

				trackGtmEvent({
					event: 'ga_event',
					category: 'Quote Request',
					action: 'finished',
					label: 'Quote Form Completed'
				});

				trackGtmEvent({
					event: 'facebook_event',
					category: 'Lead'
				});

				trackGtmEvent({
					event: 'facebook_event',
					category: trackingValues.facebook.category,
					action: 'Quote Request Completed',
					value: '0.00'
				});
			}
		} catch (error) {
			// Intentionally do nothing except print the error.
			console.log('Tracking error:', error);
		}

		if (typeof callback === 'function') {
			setTimeout(callback, delay);
		}
	}

	/**
	 * The general function used for showing any/all errors. Handles
	 * various tasking and upkeep.
	 *
	 * @name showStepErrors
	 * @method
	 *
	 * @param {Array[Error]} errors - An array of Error instances created from
	 * validating the current step.
	 */
	showStepErrors(errors) {
		this.clearErrors();

		console.log('Step errors to show:', errors);

		errors.forEach(this.showError.bind(this));
		this.currentStepElement.classList.add('has-errors');

		Ladda.stopAll();

		// Send new errors to Bugsnag.
		const currentError = errors.map(error => error.message).join();
		if (currentError !== this.lastErrorMessage) {
			this.lastErrorMessage = currentError;

			// Send to Bugsnag for us to track.
			let bugsnagError = new Error(this.lastErrorMessage);
			bugsnagError.name = 'QuoteRequestUserNotice';

			let currentQuoteRequest = [];
			for (let [key, value] of this.collect()) {
				// Don't log user passwords in Bugsnag!
				if (key === 'account_password') {
					value = '*';
				}

				currentQuoteRequest.push([key, value]);
			}

			const bugsnagOptions = {
				severity: 'info',
				metaData: {
					userNotice: {
						countNotices: errors.length,
						currentQuoteRequest: JSON.stringify(currentQuoteRequest)
					}
				},
				context: 'Quote request form'
			};

			console.log('Logging to Bugsnag:', bugsnagOptions);
			bugsnagClient.notify(bugsnagError, bugsnagOptions);
		}
	}

	/**
	 * @name showError
	 * @method
	 *
	 * @param {Error} error
	 */
	showError(error) {
		if (error.highlightElement) {
			this.showInputError(error);
		} else {
			this.showGenericError(error);
		}
	}

	/**
	 * @name showInputError
	 * @method
	 *
	 * @param {Error} error
	 */
	showInputError(error) {
		console.log('Showing input error...');
		const highlightElement = error.highlightElement;

		highlightElement.classList.add('error')

		let errorMessageElement = document.createElement('p');
		errorMessageElement.className = 'form-error js-error-message';
		errorMessageElement.dataset.forInput = highlightElement.id;
		errorMessageElement.append(error.message);

		// Insert this error message element after the highlightElement.
		highlightElement.insertAdjacentElement('afterend', errorMessageElement);
	}

	/**
	 * @name showGenericError
	 * @method
	 *
	 * @param {Error} error
	 */
	showGenericError(error) {
		console.log('Showing generic error...');
		let errorMessageElement = document.createElement('p');
		errorMessageElement.className = 'form-error js-error-message';

		if (error.relatedElement) {
			errorMessageElement.dataset.forInputName = error.relatedElement.name;
		}

		errorMessageElement.insertAdjacentHTML('afterbegin', error.message);

		// Insert this error message element above the `.button-group`
		// element. We'll use `:last-child` in case there's a situation
		// where we have more than one `.button-group` in a step.
		const buttonGroupElement = this.currentStepElement.querySelector('.button-group:last-child');
		buttonGroupElement.insertAdjacentElement('beforebegin', errorMessageElement);

		Ladda.stopAll();
	}

	/**
	 * Clear errors from a specific element or the whole current step.
	 *
	 * @name clearErrors
	 * @method
	 * @param {HTMLElement} element - Optional
	 */
	clearErrors(element) {
		if (element && element.matches('input, select, textarea')) {
			console.log('Clearing errors for:', element);
			element.classList.remove('error');

			// Remove any error messages for this specific element by ID or name
			const errorMessageElements = this.currentStepElement.querySelectorAll(`.js-error-message[data-for-input="${element.id}"], .js-error-message[data-for-input-name="${element.name}"]`);
			if (errorMessageElements.length) {
				console.log('Found error message elements to remove:', errorMessageElements);
				errorMessageElements.forEach(element => element.remove());
			}

			// If this was the last error in the step then remove the has-errors class
			const errorMessages = this.currentStepElement.querySelectorAll('.js-error-message');
			if (!errorMessages.length) {
				this.currentStepElement.classList.remove('has-errors');
			}
		} else {
			console.log('Clearing all errors from current step.');
			console.log(this.currentStepElement);
			const errorClassElements = this.currentStepElement.querySelectorAll('.error');
			errorClassElements.forEach(element => element.classList.remove('error'));

			const errorMessageElements = this.currentStepElement.querySelectorAll('.js-error-message');
			errorMessageElements.forEach(element => element.remove());

			this.currentStepElement.classList.remove('has-errors');
		}
	}
}

export {
	QuoteRequestForm
}
